import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, Field, FormikValues } from "formik";
import { MenuItem, TextField } from "@mui/material";
import "../config.styles.css";
import * as yup from "yup";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import { HERO_CAROUSEL } from "../LandingPage/constants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

const CreateContentVersionPopup = forwardRef(
    ({ show, handleCloseCB, handleAddVersion }: any, ref: any) => {
        const [open, setOpen] = useState(show);
        const [submitting, setSubmitting] = useState(false);
        const [masterData, setMasterData] = useState<any>([]);
        const { enqueueSnackbar } = useSnackbar();
        const [isEditRail, setIsEditRail] = useState(false);
        const [errorMessage, setErrorMessage] = useState<any>("");

        useEffect(() => {
            getMasterInfo();
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                setError: (message: any) => setErrorMessage(message),
            }),
            []
        );

        // set initial values
        const initialValues: FormikValues = {
            template: "",
            versionName: "",
            canonicalName: "",
        };

        const handleClose = () => {
            handleCloseCB();
        };

        // on submit form
        const handleOnSubmit = async (values: FormikValues) => {
            handleAddVersion(values);
        };

        // validation schema
        const validationSchema = yup.object({
            template: yup.string().required("Template is mandatory"),
            versionName: yup.string().required("Version name is mandatory"),
            canonicalName: yup
                .string()
                .required("Canonical name is mandatory")
                .min(8, "Canonical name must consist of at least 8 characters.")
                .max(
                    20,
                    "Canonical name must not exceed 20 characters in length"
                )
                .matches(/^[a-zA-Z0-9]+$/, {
                    message:
                        "Canonical name must consist of only numbers and letters",
                }),
        });

        // call API for getting the master information
        const getMasterInfo = async () => {
            try {
                const materInformation = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/page/master`
                );
                if (materInformation.data?.success) {
                    setMasterData(materInformation.data?.data?.pageTemplate);
                }
            } catch (error) {
                console.log("Error in getting master information", error);
            }
        };

        return (
            <div>
                {/* This is a popup modal for handling save config */}
                <BootstrapDialog
                    fullWidth
                    onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                >
                    <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={handleClose}
                    >
                        {"Create new version"}
                    </BootstrapDialogTitle>
                    <DialogContent dividers>
                        {/* Form contents for handling save configuration starts here */}
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleOnSubmit}
                        >
                            {({
                                values,
                                handleChange,
                                touched,
                                errors,
                            }: any) => (
                                <Form className="config-form">
                                    <Field
                                        name="template"
                                        as={TextField}
                                        select
                                        label="Template"
                                        value={values.template}
                                        onChange={handleChange}
                                        disabled={isEditRail}
                                        error={
                                            touched.template && errors.template
                                        }
                                        helperText={
                                            touched.template && errors.template
                                        }
                                    >
                                        {masterData.map(
                                            (master: any, index: number) => {
                                                return (
                                                    <MenuItem
                                                        key={index}
                                                        value={master.key}
                                                    >
                                                        {master.label}
                                                    </MenuItem>
                                                );
                                            }
                                        )}
                                    </Field>

                                    <Field
                                        name="versionName"
                                        as={TextField}
                                        label="Name"
                                        value={values.versionName}
                                        placeholder="Name"
                                        onChange={handleChange}
                                        error={
                                            touched.versionName &&
                                            errors.versionName
                                        }
                                        helperText={"*Display Name"}
                                    />
                                    <>
                                        <Field
                                            name="canonicalName"
                                            as={TextField}
                                            label="Canonical Name"
                                            value={values.canonicalName}
                                            placeholder="Canonical Name"
                                            onChange={(e: any) => {
                                                handleChange(e);
                                                setErrorMessage(null);
                                            }}
                                            error={
                                                (touched.canonicalName &&
                                                    errors.canonicalName) ||
                                                errorMessage
                                            }
                                            helperText={
                                                errors.canonicalName
                                                    ? errors.canonicalName
                                                    : (errorMessage &&
                                                          errorMessage) ||
                                                      "*Canonical name is the unique identifier of the version. It must be between 8-20 characters in length, consisting of letters and numbers."
                                            }
                                        />
                                    </>
                                    <Button
                                        className="max-content-width float-center"
                                        variant="outlined"
                                        type="submit"
                                        disabled={submitting}
                                    >
                                        {"Create Version"}
                                    </Button>
                                </Form>
                            )}
                        </Formik>
                        {/* Form contents for handling save configuration ends here */}
                    </DialogContent>
                </BootstrapDialog>
            </div>
        );
    }
);

export default CreateContentVersionPopup;
